import { Component } from '@theme/component';

/**
 * @typedef {object} VideoBackgroundComponentRefs
 * @property {HTMLElement[]} videoSources
 * @property {HTMLVideoElement} videoElement
 * @property {HTMLButtonElement} [togglePlayButton]
 * @property {HTMLButtonElement} [toggleMuteButton]
 * @extends Component<VideoBackgroundComponentRefs>
 */
export class VideoBackgroundComponent extends Component {
    requiredRefs = ['videoSources', 'videoElement'];

    autoplayEnabled = false;
    audioControlsEnabled = false;
    playControlsEnabled = false;

    connectedCallback() {
        super.connectedCallback();

        const { videoSources, videoElement } = this.refs;

        // Load sources
        for (const source of videoSources) {
            const { videoSource } = source.dataset;
            if (videoSource) source.setAttribute('src', videoSource);
        }

        this.autoplayEnabled = this.hasAttribute('data-autoplay');
        this.audioControlsEnabled = this.hasAttribute('data-audio-controls');
        this.playControlsEnabled = this.hasAttribute('data-play-controls');

        videoElement.load();

        // Start playing if autoplay is enabled
        if (this.autoplayEnabled) {
            videoElement.play();
        }

        this.#syncPlayIcons();
    }

    disconnectedCallback() {
        super.disconnectedCallback?.();
    }

    #syncPlayIcons = () => {
        const togglePlayButton = this.refs.togglePlayButton;
        if (!togglePlayButton) return;

        togglePlayButton.classList.toggle('paused', this.refs.videoElement.paused);
        const ariaLabel = this.refs.videoElement.paused
            ? togglePlayButton.dataset.playLabel
            : togglePlayButton.dataset.pauseLabel;
        if (ariaLabel) {
            togglePlayButton.setAttribute('aria-label', ariaLabel);
        }
    };

    togglePlay(/** @type {Event} */ e) {
        e.preventDefault();

        const { videoElement } = this.refs;

        if (videoElement.paused || videoElement.ended) {
            videoElement.play().catch(() => {
                // Play failed
            });
        } else {
            videoElement.pause();
        }
        this.#syncPlayIcons();
    }

    toggleMute(/** @type {Event} */ e) {
        e.preventDefault();

        const { videoElement } = this.refs;
        const toggleMuteButton = this.refs.toggleMuteButton;
        if (!toggleMuteButton) return;

        videoElement.muted = !videoElement.muted;

        toggleMuteButton.classList.toggle('muted', this.refs.videoElement.muted);
        const ariaLabel = this.refs.videoElement.muted
            ? toggleMuteButton.dataset.unmuteLabel
            : toggleMuteButton.dataset.muteLabel;
        if (ariaLabel) {
            toggleMuteButton.setAttribute('aria-label', ariaLabel);
        }
    }
}

if (!customElements.get('video-background-component')) {
    customElements.define('video-background-component', VideoBackgroundComponent);
}
